import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import {
  ShoppingBag,
  Zap,
  Package,
  Star,
  Flame,
  Crown,
  ChevronLeft,
  RefreshCw,
  Info,
  Users,
  User,
} from 'lucide-react';
import {
  storeService,
  type StoreProduct,
  type GroupPurchaseSummary,
} from '@/features/store/services/store.service';
import { gamificationService } from '@/features/gamification/services/gamification.service';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Progress } from '@/components/ui/progress';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

export const Route = createFileRoute('/_authenticated/store')({
  component: StorePage,
});

type PurchaseMode = 'individual' | 'group' | null;

function getRarityConfig(price: number): { label: string; color: string; border: string; glow: string } {
  if (price >= 50) return { label: 'Lendário', color: 'text-yellow-400', border: 'border-yellow-400', glow: 'shadow-yellow-400/30' };
  if (price >= 20) return { label: 'Épico', color: 'text-purple-400', border: 'border-purple-400', glow: 'shadow-purple-400/30' };
  if (price >= 10) return { label: 'Raro', color: 'text-blue-400', border: 'border-blue-400', glow: 'shadow-blue-400/30' };
  return { label: 'Comum', color: 'text-emerald-400', border: 'border-emerald-400', glow: 'shadow-emerald-400/30' };
}

function ProductCard({
  product,
  onBuy,
}: {
  product: StoreProduct;
  onBuy: (p: StoreProduct) => void;
}) {
  const price = Number(product.pointPrice);
  const rarity = getRarityConfig(price);
  const activeGroup = product.activeGroupPurchases?.[0];
  const groupProgress = activeGroup
    ? Math.min(100, (Number(activeGroup.collectedPoints) / Number(activeGroup.targetPoints)) * 100)
    : 0;

  return (
    <div
      className={`relative flex flex-col bg-slate-900 border-2 ${rarity.border} rounded-none shadow-lg ${rarity.glow} shadow-md transition-all duration-200 hover:scale-[1.02] hover:shadow-xl group`}
      style={{ imageRendering: 'pixelated' }}
    >
      <div className={`absolute top-2 left-2 z-10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${rarity.color} bg-slate-950/80 border ${rarity.border} rounded-none`}>
        {rarity.label}
      </div>

      <div className="relative h-44 bg-slate-800 overflow-hidden">
        {product.imageUrl ? (
          <img
            src={product.imageUrl}
            alt={product.name}
            className="w-full h-full object-cover"
            style={{ imageRendering: 'pixelated' }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Package size={48} className="text-slate-600" />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent pointer-events-none" />

        <div className="absolute bottom-2 right-2">
          {product.availableQty <= 0 ? (
            <span className="px-2 py-1 text-[10px] font-bold bg-red-900/90 text-red-300 border border-red-600 rounded-none">
              SEM ESTOQUE
            </span>
          ) : product.availableQty <= 5 ? (
            <span className="px-2 py-1 text-[10px] font-bold bg-orange-900/90 text-orange-300 border border-orange-600 rounded-none">
              ÚLTIMAS {product.availableQty}!
            </span>
          ) : (
            <span className="px-2 py-1 text-[10px] font-bold bg-slate-800/90 text-slate-400 border border-slate-600 rounded-none">
              QTD: {product.availableQty}
            </span>
          )}
        </div>
      </div>

      <div className="p-3 flex flex-col gap-2 flex-1">
        <h3 className="font-bold text-white text-sm leading-tight line-clamp-2" style={{ fontFamily: 'monospace' }}>
          {product.name}
        </h3>
        {product.description && (
          <p className="text-slate-400 text-xs line-clamp-2">{product.description}</p>
        )}

        {product.maxPerUser !== null && product.maxPerUser !== undefined && (
          <div className="flex items-center gap-1 text-slate-500 text-[10px]">
            <Info size={10} />
            <span>Máx. {product.maxPerUser}x por usuário por lote</span>
          </div>
        )}

        {activeGroup && (
          <div className="mt-1 space-y-1">
            <div className="flex items-center justify-between text-[10px] text-blue-300">
              <span className="flex items-center gap-1">
                <Users size={10} />
                Compra em grupo
              </span>
              <span>{Number(activeGroup.collectedPoints).toFixed(1)}/{Number(activeGroup.targetPoints).toFixed(1)} pts</span>
            </div>
            <Progress value={groupProgress} className="h-1.5" />
            <p className="text-[10px] text-slate-500">
              {activeGroup.participantCount} participante{activeGroup.participantCount !== 1 ? 's' : ''}
            </p>
          </div>
        )}
      </div>

      <div className="p-3 pt-0 flex items-center justify-between gap-2">
        <div className="flex items-center gap-1">
          <Zap size={14} className="text-yellow-400" />
          <span className={`font-bold text-lg ${rarity.color}`} style={{ fontFamily: 'monospace' }}>
            {price}
          </span>
          <span className="text-slate-500 text-xs">pts</span>
        </div>

        {product.alreadyBoughtInCurrentBatch ? (
          <span className="px-3 py-1.5 text-[11px] font-bold bg-slate-700 text-slate-400 border border-slate-600 rounded-none cursor-default">
            ✓ COMPRADO
          </span>
        ) : product.availableQty <= 0 ? (
          <span className="px-3 py-1.5 text-[11px] font-bold bg-slate-800 text-slate-500 border border-slate-700 rounded-none cursor-default">
            ESGOTADO
          </span>
        ) : (
          <button
            onClick={() => onBuy(product)}
            className="px-3 py-1.5 text-[11px] font-bold bg-emerald-600 hover:bg-emerald-500 text-white border-2 border-emerald-400 rounded-none transition-colors cursor-pointer"
          >
            COMPRAR
          </button>
        )}
      </div>
    </div>
  );
}

export function StorePage() {
  const queryClient = useQueryClient();
  const [selectedProduct, setSelectedProduct] = useState<StoreProduct | null>(null);
  const [purchaseMode, setPurchaseMode] = useState<PurchaseMode>(null);
  const [selectedGroupId, setSelectedGroupId] = useState<string | 'new'>('new');
  const [contributionAmount, setContributionAmount] = useState('');

  const { data: products = [], isLoading } = useQuery({
    queryKey: ['store-products'],
    queryFn: storeService.getProducts,
  });

  const { data: profile } = useQuery({
    queryKey: ['rpg-profile'],
    queryFn: gamificationService.getProfile,
  });

  const { data: streak } = useQuery({
    queryKey: ['user-streak'],
    queryFn: storeService.getStreak,
  });

  const { data: productGroups = [] } = useQuery({
    queryKey: ['store-group-purchases', selectedProduct?.id],
    queryFn: () => storeService.listGroupPurchases(selectedProduct!.id),
    enabled: !!selectedProduct && purchaseMode === 'group',
  });

  const buyMutation = useMutation({
    mutationFn: (productId: string) => storeService.buyProduct(productId),
    onSuccess: (data) => {
      toast.success(`Compra realizada! Você gastou ${data.pointsSpent} pts. Saldo: ${data.remainingPoints.toFixed(1)} pts`);
      invalidateStoreQueries();
      closeAllDialogs();
    },
  });

  const createGroupMutation = useMutation({
    mutationFn: ({ productId, amount }: { productId: string; amount: number }) =>
      storeService.createGroupPurchase(productId, amount),
    onSuccess: (data: any) => {
      if (data.completed) {
        toast.success(`Compra em grupo concluída com ${data.participantCount} participantes!`);
      } else {
        toast.success('Compra em grupo iniciada! Convide outros para contribuir.');
      }
      invalidateStoreQueries();
      closeAllDialogs();
    },
  });

  const contributeMutation = useMutation({
    mutationFn: ({ groupId, amount }: { groupId: string; amount: number }) =>
      storeService.contributeGroupPurchase(groupId, amount),
    onSuccess: (data: any) => {
      if (data.completed) {
        toast.success(`Meta atingida! Compra em grupo concluída com ${data.participantCount} participantes.`);
      } else {
        toast.success(`Contribuição de ${data.contributedPoints} pts registrada!`);
      }
      invalidateStoreQueries();
      closeAllDialogs();
    },
  });

  const invalidateStoreQueries = () => {
    queryClient.invalidateQueries({ queryKey: ['store-products'] });
    queryClient.invalidateQueries({ queryKey: ['rpg-profile'] });
    queryClient.invalidateQueries({ queryKey: ['store-group-purchases'] });
    queryClient.invalidateQueries({ queryKey: ['points-history'] });
  };

  const closeAllDialogs = () => {
    setSelectedProduct(null);
    setPurchaseMode(null);
    setSelectedGroupId('new');
    setContributionAmount('');
  };

  const handleBuyClick = (product: StoreProduct) => {
    setSelectedProduct(product);
    setPurchaseMode(null);
    setSelectedGroupId(product.activeGroupPurchases?.[0]?.id ?? 'new');
    setContributionAmount('');
  };

  const userPoints = Number(profile?.points ?? 0);
  const productPrice = selectedProduct ? Number(selectedProduct.pointPrice) : 0;
  const parsedContribution = Number(contributionAmount) || 0;

  const selectedExistingGroup: GroupPurchaseSummary | undefined =
    selectedGroupId !== 'new'
      ? productGroups.find((g) => g.id === selectedGroupId)
      : undefined;

  const groupRemaining = selectedExistingGroup
    ? Math.max(0, Number(selectedExistingGroup.targetPoints) - Number(selectedExistingGroup.collectedPoints))
    : productPrice;

  const groupSubmitDisabled =
    parsedContribution <= 0 || parsedContribution > userPoints || parsedContribution > groupRemaining;

  const handleGroupSubmit = () => {
    if (!selectedProduct) return;
    if (parsedContribution <= 0) {
      toast.error('Informe um valor de contribuição válido.');
      return;
    }
    if (parsedContribution > groupRemaining) {
      toast.error('O valor excede o que falta para concluir a compra em grupo.');
      return;
    }
    if (parsedContribution > userPoints) {
      toast.error('Você não tem pontos suficientes para esta contribuição.');
      return;
    }

    if (selectedGroupId === 'new') {
      createGroupMutation.mutate({
        productId: selectedProduct.id,
        amount: parsedContribution,
      });
    } else {
      contributeMutation.mutate({
        groupId: selectedGroupId,
        amount: parsedContribution,
      });
    }
  };

  return (
    <div className="min-h-screen bg-[#0A1628] text-white">
      <div className="relative overflow-hidden bg-gradient-to-r from-[#0A1628] via-[#0d1f3c] to-[#0A1628] border-b-2 border-yellow-500/30">
        <div
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage: 'linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)',
            backgroundSize: '20px 20px',
          }}
        />

        <div className="relative max-w-6xl mx-auto px-6 py-8">
          <div className="flex items-center gap-4 mb-4">
            <Link to="/tasks-history" search={{ tab: 'tasks' }} className="text-slate-400 hover:text-white transition-colors">
              <ChevronLeft size={20} />
            </Link>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-500/20 border-2 border-yellow-500 rounded-none">
                <ShoppingBag size={24} className="text-yellow-400" />
              </div>
              <div>
                <h1
                  className="text-2xl font-bold text-yellow-400 leading-tight"
                  style={{ fontFamily: 'monospace', textShadow: '2px 2px 0px #7c5500' }}
                >
                  ⚔️ LOJA DE PONTOS
                </h1>
                <p className="text-slate-400 text-xs">Troque seus pontos por recompensas épicas</p>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2 px-4 py-2 bg-slate-800/60 border border-yellow-500/30 rounded-none">
              <Zap size={16} className="text-yellow-400" />
              <span className="text-yellow-300 font-bold" style={{ fontFamily: 'monospace' }}>
                {userPoints.toFixed(1)}
              </span>
              <span className="text-slate-400 text-xs">pontos</span>
            </div>

            {profile && (
              <div className="flex items-center gap-2 px-4 py-2 bg-slate-800/60 border border-blue-500/30 rounded-none">
                <Star size={16} className="text-blue-400" />
                <span className="text-blue-300 font-bold" style={{ fontFamily: 'monospace' }}>
                  Lv. {profile.level}
                </span>
              </div>
            )}

            {streak && streak.currentStreak > 0 && (
              <div className="flex items-center gap-2 px-4 py-2 bg-slate-800/60 border border-orange-500/30 rounded-none">
                <Flame size={16} className="text-orange-400" />
                <span className="text-orange-300 font-bold" style={{ fontFamily: 'monospace' }}>
                  {streak.currentStreak} dias
                </span>
                <span className="text-slate-400 text-xs">streak</span>
              </div>
            )}

            <Link
              to="/tasks-history"
              search={{ tab: 'purchases' }}
              className="flex items-center gap-1 text-slate-400 hover:text-white text-xs transition-colors ml-auto"
            >
              <RefreshCw size={12} />
              Minhas compras
            </Link>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8">
        {isLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="h-72 bg-slate-800 animate-pulse border-2 border-slate-700 rounded-none" />
            ))}
          </div>
        ) : products.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <div className="p-6 bg-slate-800 border-2 border-slate-700 rounded-none mb-4">
              <Package size={48} className="text-slate-600" />
            </div>
            <h3 className="text-slate-400 font-bold text-lg" style={{ fontFamily: 'monospace' }}>
              LOJA VAZIA
            </h3>
            <p className="text-slate-600 text-sm mt-2">Nenhum produto disponível no momento.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {products.map((product) => (
              <ProductCard key={product.id} product={product} onBuy={handleBuyClick} />
            ))}
          </div>
        )}
      </div>

      {/* Purchase mode selection */}
      <Dialog open={!!selectedProduct && purchaseMode === null} onOpenChange={() => closeAllDialogs()}>
        <DialogContent size="sm" className="bg-slate-900 border-2 border-yellow-500/50 rounded-none text-white">
          <DialogHeader>
            <DialogTitle className="text-yellow-400" style={{ fontFamily: 'monospace' }}>
              COMO DESEJA COMPRAR?
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Escolha entre compra individual ou em grupo com contribuições de todo o sistema.
            </DialogDescription>
          </DialogHeader>

          {selectedProduct && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 py-2">
              <button
                onClick={() => setPurchaseMode('individual')}
                disabled={userPoints < productPrice}
                className="flex flex-col items-start gap-2 p-4 border-2 border-emerald-500/50 bg-emerald-950/30 hover:bg-emerald-900/40 disabled:opacity-50 disabled:cursor-not-allowed rounded-none text-left transition-colors"
              >
                <User size={20} className="text-emerald-400" />
                <span className="font-bold text-emerald-300">Individual</span>
                <span className="text-xs text-slate-400">
                  Gastar {selectedProduct.pointPrice} pts agora
                </span>
              </button>

              <button
                onClick={() => setPurchaseMode('group')}
                className="flex flex-col items-start gap-2 p-4 border-2 border-blue-500/50 bg-blue-950/30 hover:bg-blue-900/40 rounded-none text-left transition-colors"
              >
                <Users size={20} className="text-blue-400" />
                <span className="font-bold text-blue-300">Em grupo</span>
                <span className="text-xs text-slate-400">
                  Arrecadar com outros usuários
                </span>
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Individual purchase */}
      <Dialog open={!!selectedProduct && purchaseMode === 'individual'} onOpenChange={() => closeAllDialogs()}>
        <DialogContent size="sm" busy={buyMutation.isPending} className="bg-slate-900 border-2 border-yellow-500/50 rounded-none text-white">
          <DialogHeader>
            <DialogTitle className="text-yellow-400 flex items-center gap-2" style={{ fontFamily: 'monospace' }}>
              <Crown size={18} />
              CONFIRMAR COMPRA INDIVIDUAL
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Você está prestes a comprar este item da loja.
            </DialogDescription>
          </DialogHeader>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (selectedProduct && userPoints >= productPrice) buyMutation.mutate(selectedProduct.id);
            }}
          >
          {selectedProduct && (
            <div className="flex flex-col gap-4 py-2">
              <div className="flex gap-4 p-4 bg-slate-800 border border-slate-700 rounded-none">
                <div className="flex-1">
                  <h4 className="font-bold text-white" style={{ fontFamily: 'monospace' }}>{selectedProduct.name}</h4>
                  <div className="flex items-center gap-1 mt-2">
                    <Zap size={14} className="text-yellow-400" />
                    <span className="text-yellow-400 font-bold">{selectedProduct.pointPrice} pts</span>
                  </div>
                </div>
              </div>

              <div className="flex justify-between text-sm text-slate-400 px-1">
                <span>Seus pontos:</span>
                <span className="text-white font-bold">{userPoints.toFixed(1)} pts</span>
              </div>
              <div className="flex justify-between text-sm border-t border-slate-700 pt-2 px-1">
                <span className="text-slate-400">Após compra:</span>
                <span className="text-emerald-400 font-bold">
                  {(userPoints - productPrice).toFixed(1)} pts
                </span>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={() => setPurchaseMode(null)} className="rounded-none border-slate-600 text-slate-400 hover:text-white">
              VOLTAR
            </Button>
            <Button
              type="submit"
              loading={buyMutation.isPending}
              disabled={userPoints < productPrice}
              className="rounded-none bg-emerald-600 hover:bg-emerald-500 border-2 border-emerald-400 text-white font-bold"
            >
              CONFIRMAR COMPRA
            </Button>
          </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Group purchase */}
      <Dialog open={!!selectedProduct && purchaseMode === 'group'} onOpenChange={() => closeAllDialogs()}>
        <DialogContent size="md" busy={createGroupMutation.isPending || contributeMutation.isPending} className="bg-slate-900 border-2 border-blue-500/50 rounded-none text-white">
          <DialogHeader>
            <DialogTitle className="text-blue-400 flex items-center gap-2" style={{ fontFamily: 'monospace' }}>
              <Users size={18} />
              COMPRA EM GRUPO
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Crie uma arrecadação ou contribua para uma existente. Todos do sistema podem participar.
            </DialogDescription>
          </DialogHeader>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (groupSubmitDisabled) return;
              handleGroupSubmit();
            }}
          >
          {selectedProduct && (
            <div className="flex flex-col gap-4 py-2">
              <div className="p-3 bg-slate-800 border border-slate-700 rounded-none">
                <h4 className="font-bold text-white text-sm">{selectedProduct.name}</h4>
                <p className="text-xs text-slate-400 mt-1">Meta: {selectedProduct.pointPrice} pts</p>
              </div>

              <div className="space-y-2">
                <label className="text-xs text-slate-400 font-bold uppercase">Escolha a arrecadação</label>
                <select
                  value={selectedGroupId}
                  onChange={(e) => setSelectedGroupId(e.target.value as string | 'new')}
                  className="w-full bg-slate-800 border border-slate-600 text-white text-sm p-2 rounded-none"
                >
                  <option value="new">Criar nova arrecadação</option>
                  {productGroups.map((group) => (
                    <option key={group.id} value={group.id}>
                      {Number(group.collectedPoints).toFixed(1)}/{Number(group.targetPoints).toFixed(1)} pts · {group.participantCount} participantes
                    </option>
                  ))}
                </select>
              </div>

              {selectedExistingGroup && (
                <div className="space-y-2">
                  <div className="flex justify-between text-xs text-blue-300">
                    <span>Progresso</span>
                    <span>
                      {Number(selectedExistingGroup.collectedPoints).toFixed(1)}/
                      {Number(selectedExistingGroup.targetPoints).toFixed(1)} pts
                    </span>
                  </div>
                  <Progress
                    value={Math.min(
                      100,
                      (Number(selectedExistingGroup.collectedPoints) /
                        Number(selectedExistingGroup.targetPoints)) *
                        100,
                    )}
                    className="h-2"
                  />
                  <p className="text-[11px] text-slate-500">
                    Faltam {groupRemaining.toFixed(1)} pts para concluir
                  </p>
                </div>
              )}

              <div className="space-y-2">
                <label className="text-xs text-slate-400 font-bold uppercase">Sua contribuição (pts)</label>
                <Input
                  type="number"
                  min="0.1"
                  step="0.1"
                  value={contributionAmount}
                  onChange={(e) => setContributionAmount(e.target.value)}
                  placeholder={`Máx. ${Math.min(userPoints, groupRemaining).toFixed(1)} pts`}
                  className="rounded-none bg-slate-800 border-slate-600 text-white"
                />
                <p className="text-[11px] text-slate-500">
                  Seu saldo: {userPoints.toFixed(1)} pts
                </p>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={() => setPurchaseMode(null)} className="rounded-none border-slate-600 text-slate-400 hover:text-white">
              VOLTAR
            </Button>
            <Button
              type="submit"
              loading={createGroupMutation.isPending || contributeMutation.isPending}
              disabled={groupSubmitDisabled}
              className="rounded-none bg-blue-600 hover:bg-blue-500 border-2 border-blue-400 text-white font-bold"
            >
              {selectedGroupId === 'new' ? 'CRIAR E CONTRIBUIR' : 'CONTRIBUIR'}
            </Button>
          </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
